// 管理画面と連動する絞り込み項目(URL パラメータ名 = 項目名、値 = URL用キー)
const SNAP_FACET_GROUPS = ['scene', 'season', 'footwear', 'gender', 'brand'];

class SnapCollectionManager {
  constructor() {
    this.config = window.snapCollectionConfig;
    this.state = {
      filters: {},
      order: '新着順',
      currentPage: 1,
      isLoading: false,
      productPageProductNumber: '',
      // 管理画面連動の項目ごとに選択中のキー
      facets: Object.fromEntries(SNAP_FACET_GROUPS.map((group) => [group, []])),
    };
    // 件数 API の結果(選択肢名の表示用)と、古い応答を捨てるための連番
    this.facetOptions = {};
    this.facetRequestSeq = 0;
    this.lastFacetQuery = null;
    this.snapRequestSeq = 0;
    // 「商品で探す」: 商品キー → 商品名、候補一覧の状態
    this.productNames = {};
    this.productSearch = { timer: null, seq: 0, items: [], active: -1 };
    this.init();
  }

  async init() {
    try {
      await this.loadStateFromURL();
      this.setupEventListeners();
      this.setupProductSearch();
      this.loadProductNames();
      this.renderActiveFilters();
      this.updateFilterUI();

      if (!this.state.productPageProductNumber) {
        await this.fetchSnapCollection();
      } else {
        this.fetchFacets();
      }
    } catch (error) {
      console.error('初期化エラー:', error);
      this.showError('ページの初期化に失敗しました');
    }
  }

  async loadStateFromURL() {
    const urlParams = new URLSearchParams(window.location.search);

    const orderParam = urlParams.get('order');
    if (orderParam) {
      this.state.order = this.normalizeOrderParam(orderParam);
      this.updateSortDisplay(this.state.order);
    }

    await Promise.all([
      this.restoreFiltersFromURL('height', this.config.heightOptions, urlParams),
      this.restoreFiltersFromURL('tag', this.config.tagOptions, urlParams),
      this.restoreFiltersFromURL('product', this.config.productOptions, urlParams),
      this.restoreFiltersFromURL('keyword', this.config.tagOptions, urlParams),
    ]);

    for (const group of SNAP_FACET_GROUPS) {
      const values = urlParams.getAll(group).flatMap((v) => v.split(','));
      this.state.facets[group] = [...new Set(values.map((v) => v.trim()).filter(Boolean))];
    }

    const pageParam = urlParams.get('page');
    if (pageParam) {
      this.state.currentPage = Math.max(1, parseInt(pageParam) || 1);
    }

    await this.handleProductPageParams(urlParams);
  }

  async restoreFiltersFromURL(paramName, validOptions, urlParams) {
    let param = urlParams.get(paramName) || urlParams.get(`filter.${paramName}`);
    let multipleValues = [];

    const standardParams = urlParams.getAll(paramName);
    const filteredParams = urlParams.getAll(`filter.${paramName}`);

    multipleValues = [...standardParams, ...filteredParams];

    if (param) {
      const singleValues = param.split(',');
      multipleValues = [...multipleValues, ...singleValues];
    }

    const uniqueValues = [...new Set(multipleValues)];

    for (const value of uniqueValues) {
      if (!value) continue;

      const decodedValue = decodeURIComponent(value);
      const isValidOption = validOptions.includes(decodedValue);
      const isInHTML = this.isValueInHTML(decodedValue, paramName);

      if (isValidOption || isInHTML) {
        this.state.filters[decodedValue] = true;
        this.state.filters[`${decodedValue}__source`] = paramName;
        this.setCheckboxState(decodedValue, true);
      } else {
        this.state.filters[decodedValue] = true;
        this.state.filters[`${decodedValue}__source`] = paramName;
      }
    }
  }

  isValueInHTML(value, paramName) {
    const selector = `input[name="filter.${paramName}"][value="${value}"], input[name="${paramName}"][value="${value}"]`;
    const element = document.querySelector(selector);
    return element !== null;
  }

  async handleProductPageParams(urlParams) {
    const name = urlParams.get('name');
    if (!name) return;

    this.state.productPageProductNumber = urlParams.get('model_number') || '';
    const decodedName = decodeURIComponent(name);

    const labels = document.getElementsByTagName('label');
    let filterApplied = false;

    for (const label of labels) {
      if (label.textContent.trim() === decodedName) {
        const checkboxId = label.getAttribute('for');
        const checkbox = document.getElementById(checkboxId);
        if (checkbox && !filterApplied) {
          checkbox.checked = true;
          await this.addFilter(checkbox.value);
          filterApplied = true;
          break;
        }
      }
    }

    if (!filterApplied && this.state.productPageProductNumber) {
      await this.addFilter(this.state.productPageProductNumber);
    }
  }

  setupEventListeners() {
    window.addEventListener('popstate', () => {
      location.reload();
    });
  }

  async fetchSnapCollection() {
    const seq = ++this.snapRequestSeq;

    this.state.isLoading = true;
    this.showLoading();
    this.fetchFacets();

    try {
      const url = this.buildApiUrl();
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (seq !== this.snapRequestSeq) return;
      this.renderSnapCollection(data);
      this.updatePagination(data);
    } catch (error) {
      if (seq !== this.snapRequestSeq) return;
      console.error('API呼び出しエラー:', error);
      this.showError('データの取得に失敗しました。しばらくしてから再度お試しください。');
    } finally {
      if (seq === this.snapRequestSeq) {
        this.state.isLoading = false;
        this.hideLoading();
      }
    }
  }

  buildApiUrl() {
    const params = new URLSearchParams();

    const heightFilters = this.getActiveFiltersBySource('height');
    const tagFilters = this.getActiveFiltersBySource('tag');
    const productFilters = this.getActiveFiltersBySource('product');
    const keywordFilters = this.getActiveFiltersBySource('keyword');

    // フィルター未選択時は全身長帯を指定してAPIに送る（未指定だと170cm以上が返らないため）
    if (heightFilters.length > 0) {
      params.set('height', heightFilters.join(','));
    } else if (this.config.heightOptions && this.config.heightOptions.length > 0) {
      params.set('height', this.config.heightOptions.join(','));
    }

    if (tagFilters.length > 0) {
      params.set('tag', tagFilters.join(','));
    }

    if (productFilters.length > 0) {
      params.set('product', productFilters.join(','));
    }

    if (keywordFilters.length > 0) {
      params.set('keyword', keywordFilters.join(','));
    }

    this.appendFacetParams(params);

    if (this.state.order) {
      params.set('order', this.state.order);
    }

    params.set('page', this.state.currentPage.toString());

    if (this.state.productPageProductNumber) {
      params.set('sku', this.state.productPageProductNumber);
    }

    return `${this.config.apiBaseUrl}?${params.toString()}`;
  }

  appendFacetParams(params) {
    for (const group of SNAP_FACET_GROUPS) {
      if (this.state.facets[group].length > 0) {
        params.set(group, this.state.facets[group].join(','));
      }
    }
  }

  // 件数 API 用のパラメータ(並び順・ページは件数に関係ないので含めない)
  buildFacetsApiUrl() {
    const url = new URL(this.buildApiUrl(), window.location.href);
    url.searchParams.delete('order');
    url.searchParams.delete('page');
    return `${this.config.facetsApiUrl}?${url.searchParams.toString()}`;
  }

  async fetchFacets() {
    if (!this.config.facetsApiUrl) return;
    const url = this.buildFacetsApiUrl();
    if (url === this.lastFacetQuery) return;
    this.lastFacetQuery = url;
    const seq = ++this.facetRequestSeq;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (seq !== this.facetRequestSeq) return; // 新しい条件で取り直し中
      this.facetOptions = data.groups || {};
      this.renderFacets();
      this.renderActiveFilters();
    } catch (error) {
      console.error('絞り込み件数の取得エラー:', error);
      this.lastFacetQuery = null;
    }
  }

  renderFacets() {
    for (const group of SNAP_FACET_GROUPS) {
      const container = document.querySelector(`[data-snap-facet-options="${group}"]`);
      if (!container) continue;
      const options = this.facetOptions[group] || [];
      const selected = this.state.facets[group];

      container.innerHTML = '';
      options.forEach((option, index) => {
        const id = `checkbox-snap-facet-${group}-${index}`;
        const isChecked = selected.includes(option.code);
        const row = document.createElement('div');
        row.className = 'checkbox-control';

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.id = id;
        input.className = 'dot-checkbox';
        input.name = `snap-facet-${group}`;
        input.value = option.code;
        input.checked = isChecked;
        input.addEventListener('change', () => this.toggleFacet(group, option.code, input.checked));

        const label = document.createElement('label');
        label.htmlFor = id;
        label.textContent = option.name;
        this.appendFacetCount(label, option.count);

        row.append(input, label);
        this.setFacetAvailability(row, input, option.count);
        container.appendChild(row);
      });
    }

    // スタイリング(テーマ設定のカテゴリー一覧)は既存のチェックボックスに件数だけ付ける
    const stylingCounts = new Map((this.facetOptions.styling || []).map((o) => [o.code, o.count]));
    document.querySelectorAll('[data-snap-facet="styling"] input[name="filter.tag"]').forEach((input) => {
      if (!stylingCounts.has(input.value)) return;
      const label = document.querySelector(`label[for="${input.id}"]`);
      const count = stylingCounts.get(input.value);
      if (label) this.appendFacetCount(label, count);
      this.setFacetAvailability(input.closest('.checkbox-control'), input, count);
    });
  }

  appendFacetCount(label, count) {
    let span = label.querySelector('.snap-facet-count');
    if (!span) {
      span = document.createElement('span');
      span.className = 'snap-facet-count';
      label.appendChild(span);
    }
    span.textContent = `(${count})`;
  }

  // 0件の選択肢はグレーアウトして選べなくする(選択中のものは外せるように残す)
  setFacetAvailability(row, input, count) {
    const unavailable = count === 0 && !input.checked;
    input.disabled = unavailable;
    if (row) row.classList.toggle('is-disabled', unavailable);
  }

  async toggleFacet(group, code, checked) {
    const current = this.state.facets[group];
    this.state.facets[group] = checked
      ? [...new Set([...current, code])]
      : current.filter((value) => value !== code);
    this.state.currentPage = 1;

    const input = document.querySelector(`input[name="snap-facet-${group}"][value="${code}"]`);
    if (input) input.checked = checked;

    this.renderActiveFilters();
    this.updateFilterUI();
    this.updateURL();
    await this.fetchSnapCollection();
  }

  facetOptionName(group, code) {
    const option = (this.facetOptions[group] || []).find((o) => o.code === code);
    return option ? option.name : code;
  }

  // ===== 商品で探す =====
  setupProductSearch() {
    const input = document.getElementById('snap-product-search-input');
    if (!input || !this.config.productSuggestUrl) return;

    input.addEventListener('input', (event) => {
      if (event.isComposing) return; // 日本語の変換中は確定を待つ
      this.scheduleProductSearch(input.value);
    });
    input.addEventListener('compositionend', () => this.scheduleProductSearch(input.value));
    input.addEventListener('focus', () => {
      if (input.value.trim()) this.scheduleProductSearch(input.value);
    });
    input.addEventListener('keydown', (event) => {
      if (event.isComposing) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.moveProductActive(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveProductActive(-1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const { items, active } = this.productSearch;
        const item = items[active >= 0 ? active : 0];
        if (item) this.selectProduct(item);
      } else if (event.key === 'Escape') {
        this.hideProductSuggestions();
      }
    });
    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-snap-product-search]')) this.hideProductSuggestions();
    });
  }

  scheduleProductSearch(value) {
    clearTimeout(this.productSearch.timer);
    const query = value.trim();
    if (!query) {
      this.productSearch.seq++;
      this.hideProductSuggestions();
      return;
    }
    this.productSearch.timer = setTimeout(() => this.fetchProductSuggestions(query), 250);
  }

  // 候補の件数は、商品以外の現在の条件で数える(API 側で商品の条件を外す)
  buildProductSuggestUrl(extraParams) {
    const url = new URL(this.buildApiUrl(), window.location.href);
    url.searchParams.delete('order');
    url.searchParams.delete('page');
    for (const [key, value] of Object.entries(extraParams)) url.searchParams.set(key, value);
    return `${this.config.productSuggestUrl}?${url.searchParams.toString()}`;
  }

  async fetchProductSuggestions(query) {
    const seq = ++this.productSearch.seq;
    try {
      const response = await fetch(this.buildProductSuggestUrl({ q: query }));
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (seq !== this.productSearch.seq) return;
      const products = data.products || [];
      products.forEach((p) => (this.productNames[p.code] = p.name));
      this.renderProductSuggestions(products);
    } catch (error) {
      console.error('商品候補の取得エラー:', error);
    }
  }

  renderProductSuggestions(products) {
    const list = document.getElementById('snap-product-search-list');
    const input = document.getElementById('snap-product-search-input');
    if (!list || !input) return;

    const selected = this.getActiveFiltersBySource('product');
    this.productSearch.items = products.filter((p) => !selected.includes(p.code));
    this.productSearch.active = -1;
    list.innerHTML = '';

    if (this.productSearch.items.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'snap-product-search__empty';
      empty.textContent = '該当する商品のスナップはありません';
      list.appendChild(empty);
    }

    this.productSearch.items.forEach((product, index) => {
      const li = document.createElement('li');
      li.id = `snap-product-option-${index}`;
      li.className = 'snap-product-search__option';
      li.setAttribute('role', 'option');
      const text = document.createElement('span');
      text.className = 'snap-product-search__name';
      text.textContent = product.name;
      const code = document.createElement('span');
      code.className = 'snap-product-search__code';
      code.textContent = product.code;
      text.appendChild(code);
      const count = document.createElement('span');
      count.className = 'snap-product-search__count';
      count.textContent = `${product.count}件`;
      li.append(text, count);
      // mousedown: 入力欄のフォーカスが外れる前に選択する
      li.addEventListener('mousedown', (event) => {
        event.preventDefault();
        this.selectProduct(product);
      });
      list.appendChild(li);
    });

    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  moveProductActive(delta) {
    const { items } = this.productSearch;
    if (items.length === 0) return;
    const next = (this.productSearch.active + delta + items.length) % items.length;
    this.productSearch.active = next;
    document.querySelectorAll('.snap-product-search__option').forEach((li, index) => {
      li.classList.toggle('is-active', index === next);
      li.setAttribute('aria-selected', index === next ? 'true' : 'false');
    });
    const input = document.getElementById('snap-product-search-input');
    if (input) input.setAttribute('aria-activedescendant', `snap-product-option-${next}`);
    document.getElementById(`snap-product-option-${next}`)?.scrollIntoView({ block: 'nearest' });
  }

  hideProductSuggestions() {
    const list = document.getElementById('snap-product-search-list');
    const input = document.getElementById('snap-product-search-input');
    if (list) list.hidden = true;
    if (input) {
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }
    this.productSearch.active = -1;
  }

  async selectProduct(product) {
    this.productNames[product.code] = product.name;
    const input = document.getElementById('snap-product-search-input');
    if (input) input.value = '';
    this.productSearch.seq++;
    this.hideProductSuggestions();
    await this.addFilter(product.code, 'product');
  }

  // URL や商品ページから来た商品キーの名前を取得して表示する
  async loadProductNames() {
    const codes = this.getActiveFiltersBySource('product').filter((code) => !this.productNames[code]);
    if (codes.length === 0 || !this.config.productSuggestUrl) return;
    try {
      const response = await fetch(this.buildProductSuggestUrl({ codes: codes.join(',') }));
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      (data.products || []).forEach((p) => (this.productNames[p.code] = p.name));
      this.renderActiveFilters();
    } catch (error) {
      console.error('商品名の取得エラー:', error);
    }
  }

  renderSelectedProducts() {
    const container = document.querySelector('[data-snap-product-selected]');
    if (!container) return;
    container.innerHTML = '';
    for (const code of this.getActiveFiltersBySource('product')) {
      const row = document.createElement('div');
      row.className = 'snap-product-search__selected-item';
      const name = document.createElement('span');
      name.textContent = this.productNames[code] || code;
      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-label', `${this.productNames[code] || code} を外す`);
      button.textContent = '×';
      button.addEventListener('click', () => this.removeFilter(code));
      row.append(name, button);
      container.appendChild(row);
    }
  }

  renderSnapCollection(data) {
    const snapData = data.data || [];
    const container = document.getElementById('snap-collection-list');

    if (!container) {
      throw new Error('コンテナ要素が見つかりません');
    }

    let rank = (this.state.currentPage - 1) * this.config.itemsPerPage;
    const htmlParts = [];

    for (const snap of snapData) {
      rank++;
      const itemHTML = this.createSnapItemHTML(snap, rank);
      htmlParts.push(itemHTML);
    }

    container.innerHTML = htmlParts.join('');
  }

  createSnapItemHTML(snap, rank) {
    const newLabelHTML = this.isNewItem(snap.created_at) ? '<div class="ProductItem__new_Label">NEW</div>' : '';
    // 商品で絞り込んだときは、その商品の着用サイズも出す(例: 158cm / S)
    const info = snap.worn_size ? `${snap.height}cm / ${this.escapeHTML(snap.worn_size)}` : `${snap.height}cm`;
    return `
      <div class="ProductItem" style="visibility: inherit; opacity: 1; transform: matrix(1, 0, 0, 1, 0, 0);">
        <div class="ProductItem__Wrapper">
          <a href="https://store.soeju.com/pages/snap?id=${9000 + snap.id}" class="ProductItem__ImageWrapper ProductItem__ImageWrapper--withAlternateImage">
            ${newLabelHTML}
            <div class="AspectRatio AspectRatio--withFallback">
              <img class="ProductItem__Image" src="${snap.top_img_url}" alt="スナップ画像">
            </div>
          </a>
          <div class="ProductItem__LabelList"></div>
          <div class="ProductItem__Info">
            <span class="ProductItem__Price Price Text--subdued">${info}</span>
          </div>
        </div>
      </div>`;
  }

  escapeHTML(value) {
    const div = document.createElement('div');
    div.textContent = String(value);
    return div.innerHTML;
  }

  isNewItem(itemDateStr) {
    if (!itemDateStr) return false;

    try {
      const itemDate = new Date(itemDateStr);
      const now = new Date();

      if (isNaN(itemDate.getTime())) return false;

      const diffTime = now - itemDate;
      const diffDays = diffTime / (1000 * 60 * 60 * 24);

      return diffDays >= 0 && diffDays <= this.config.newItemThresholdDays;
    } catch (error) {
      console.error('日付解析エラー:', error);
      return false;
    }
  }

  async addFilter(filterValue, sourceParam = null) {
    this.state.filters[filterValue] = true;

    if (sourceParam) {
      this.state.filters[`${filterValue}__source`] = sourceParam;
    } else {
      if (/^\d+cm~\d*cm*$|^~\d+cm$|^\d+cm~$/.test(filterValue)) {
        this.state.filters[`${filterValue}__source`] = 'height';
      } else if (/^S-\d{2}-[A-Z]{2,3}-\d{3}/.test(filterValue)) {
        this.state.filters[`${filterValue}__source`] = 'product';
      } else {
        this.state.filters[`${filterValue}__source`] = 'tag';
      }
    }

    this.state.currentPage = 1;

    this.renderActiveFilters();
    this.updateFilterUI();
    this.updateURL();
    await this.fetchSnapCollection();
  }

  async removeFilter(filterValue) {
    delete this.state.filters[filterValue];
    delete this.state.filters[`${filterValue}__source`];
    this.state.currentPage = 1;

    this.setCheckboxState(filterValue, false);
    this.renderActiveFilters();
    this.updateFilterUI();
    this.updateURL();
    await this.fetchSnapCollection();
  }

  async clearAllFilters() {
    this.state.filters = {};
    this.state.facets = Object.fromEntries(SNAP_FACET_GROUPS.map((group) => [group, []]));
    this.state.currentPage = 1;

    document.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      checkbox.checked = false;
    });

    this.renderActiveFilters();
    this.updateFilterUI();
    this.updateURL();
    await this.fetchSnapCollection();
  }

  async updateSort(sortValue) {
    this.state.order = sortValue;
    this.state.currentPage = 1;

    this.updateSortDisplay(sortValue);
    this.updateURL();
    this.closePopover();
    await this.fetchSnapCollection();
  }

  async changePage(pageNum) {
    this.state.currentPage = Math.max(1, pageNum);
    this.updateURL();
    await this.fetchSnapCollection();

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  normalizeOrderParam(orderParam) {
    const orderMap = {
      new: '新着順',
      old: '古い順',
      popular: '人気順',
      新着順: '新着順',
      古い順: '古い順',
      人気順: '人気順',
    };
    return orderMap[orderParam] || '新着順';
  }

  serializeOrderParam(orderValue) {
    const orderMap = {
      '新着順': 'new',
      '古い順': 'old',
      '人気順': 'popular',
    };
    return orderMap[orderValue] || 'new';
  }

  getActiveFiltersBySource(sourceType) {
    const actualFilters = Object.keys(this.state.filters).filter(
      (key) => this.state.filters[key] && !key.includes('__source'),
    );

    return actualFilters.filter((filterValue) => {
      const source = this.state.filters[`${filterValue}__source`];
      return source === sourceType;
    });
  }

  getActiveFilters(filterArray) {
    const actualFilters = Object.keys(this.state.filters).filter(
      (key) => this.state.filters[key] && !key.includes('__source'),
    );

    const configuredFilters = filterArray.filter((item) => actualFilters.includes(item));

    return configuredFilters;
  }

  setCheckboxState(value, checked) {
    const checkbox = document.querySelector(`input[value="${value}"]`);
    if (checkbox) {
      checkbox.checked = checked;
    }
  }

  updateSortDisplay(sortType) {
    const displayElement = document.getElementById('sort-by-selected-value');
    if (displayElement) {
      displayElement.textContent = sortType;
    }
  }

  renderActiveFilters() {
    const container = document.getElementById('active-filters-container');
    if (!container) return;

    container.innerHTML = '';

    const actualFilters = Object.keys(this.state.filters).filter(
      (key) => this.state.filters[key] && !key.includes('__source'),
    );

    actualFilters.forEach((filterValue) => {
      // 選択中の条件は項目名(「身長:」「商品:」など)を付けず、値だけを出す
      const isProduct = this.state.filters[`${filterValue}__source`] === 'product';
      const label = isProduct ? this.productNames[filterValue] || filterValue : filterValue;
      const span = document.createElement('span');
      span.className = 'tag-list__item';
      span.setAttribute('data-filter', filterValue);
      span.textContent = `${label} `;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tag-list__remove';
      button.textContent = '×';
      button.addEventListener('click', () => this.removeFilter(filterValue));
      span.appendChild(button);
      container.appendChild(span);
    });
    this.renderSelectedProducts();

    for (const group of SNAP_FACET_GROUPS) {
      for (const code of this.state.facets[group]) {
        const span = document.createElement('span');
        span.className = 'tag-list__item';
        span.textContent = `${this.facetOptionName(group, code)} `;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'tag-list__remove';
        button.textContent = '×';
        button.addEventListener('click', () => this.toggleFacet(group, code, false));
        span.appendChild(button);
        container.appendChild(span);
      }
    }
  }

  updateFilterUI() {
    const actualFilters = Object.keys(this.state.filters).filter(
      (key) => this.state.filters[key] && !key.includes('__source'),
    );

    const facetCount = SNAP_FACET_GROUPS.reduce((sum, group) => sum + this.state.facets[group].length, 0);
    const activeCount = actualFilters.length + facetCount;
    const clearButton = document.getElementById('drawer-clear-button');

    if (clearButton) {
      clearButton.style.display = activeCount > 0 ? 'block' : 'none';
    }
  }

  updateURL() {
    const params = new URLSearchParams();

    if (this.state.order) {
      params.set('order', this.serializeOrderParam(this.state.order));
    }

    const heightFilters = this.getActiveFiltersBySource('height');
    if (heightFilters.length > 0) {
      params.set('height', heightFilters.join(','));
    }

    const tagFilters = this.getActiveFiltersBySource('tag');
    if (tagFilters.length > 0) {
      params.set('tag', tagFilters.join(','));
    }

    const productFilters = this.getActiveFiltersBySource('product');
    if (productFilters.length > 0) {
      params.set('product', productFilters.join(','));
    }

    const keywordFilters = this.getActiveFiltersBySource('keyword');
    if (keywordFilters.length > 0) {
      params.set('keyword', keywordFilters.join(','));
    }

    this.appendFacetParams(params);

    if (this.state.currentPage > 1) {
      params.set('page', this.state.currentPage.toString());
    }

    const newURL = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.pushState({ path: newURL }, '', newURL);
  }

  updatePagination(data) {
    const currentPage = data.current_page;
    const perPage = data.per_page;
    const total = data.total;
    const maxPageNum = Math.floor((total - 1) / perPage) + 1;

    let paginationContent = '';

    if (currentPage > 1) {
      paginationContent += `<a href="javascript:void(0);" class="Pagination__NavItem NavItem-link c_pagination-prev" rel="prev" title="前のページへ" onclick="snapManager.changePage(${currentPage - 1})">
        <svg xmlns="http://www.w3.org/2000/svg" width="7" height="12" viewBox="0 0 7 12" fill="none">
          <path d="M6 1L1 6L6 11" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </a>`;
    }

    if (currentPage > 3) {
      paginationContent +=
        '<a href="javascript:void(0);" class="Pagination__NavItem NavItem-link" title="1へ" onclick="snapManager.changePage(1)">1</a>';
      if (currentPage > 4) {
        paginationContent += '<span class="Pagination__NavItem">…</span>';
      }
    }

    for (let i = Math.max(1, currentPage - 2); i <= Math.min(maxPageNum, currentPage + 2); i++) {
      if (i === currentPage) {
        paginationContent += '<span class="Pagination__NavItem is-active">' + currentPage + '</span>';
      } else {
        paginationContent +=
          '<a href="javascript:void(0);" class="Pagination__NavItem NavItem-link" title="' +
          i +
          'へ" onclick="snapManager.changePage(' +
          i +
          ')">' +
          i +
          '</a>';
      }
    }

    if (currentPage < maxPageNum - 2) {
      if (currentPage < maxPageNum - 3) {
        paginationContent += '<span class="Pagination__NavItem is-ellipsis">…</span>';
      }
      paginationContent +=
        '<a href="javascript:void(0);" class="Pagination__NavItem NavItem-link" title="' +
        maxPageNum +
        'へ" onclick="snapManager.changePage(' +
        maxPageNum +
        ')">' +
        maxPageNum +
        '</a>';
    }

    if (currentPage < maxPageNum) {
      paginationContent += `<a href="javascript:void(0);" class="Pagination__NavItem NavItem-link c_pagination-next" rel="next" title="次のページへ" onclick="snapManager.changePage(${currentPage + 1})">
        <svg xmlns="http://www.w3.org/2000/svg" width="7" height="12" viewBox="0 0 7 12" fill="none">
          <path d="M1 1L6 6L1 11" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </a>`;
    }

    const paginationElement = document.getElementById('Pagination__Nav');
    if (paginationElement) {
      paginationElement.innerHTML = paginationContent;
    }
  }

  closePopover() {
    const sortPopover = document.getElementById('sort-by-popover');
    if (sortPopover) {
      sortPopover.classList.remove('is-open');

      const overlay = sortPopover.querySelector('.popover__overlay');
      if (overlay) {
        overlay.style.display = 'none';
      }
    }

    const button = document.querySelector('[aria-controls="sort-by-popover"]');
    if (button) {
      button.setAttribute('aria-expanded', 'false');
    }
  }

  showLoading() {
    const container = document.getElementById('snap-collection-list');
    if (container) {
      container.innerHTML = '<div class="loading-overlay"><div class="loading-spinner"></div></div>';
    }
  }

  hideLoading() {
    // ローディングは新しいコンテンツで置き換えられる
  }

  showError(message) {
    const container = document.getElementById('snap-collection-list');
    if (container) {
      container.innerHTML = `
        <div class="error-message">
          <p>${message}</p>
          <button onclick="snapManager.fetchSnapCollection()">再試行</button>
        </div>
      `;
    }
  }
}

let snapManager;

document.addEventListener('DOMContentLoaded', () => {
  snapManager = new SnapCollectionManager();
});

function sortSnapCollectionWithURL(sortValue) {
  snapManager?.updateSort(sortValue);
}

function handleFilterChange(checkbox, filterValue) {
  if (!snapManager) return;

  const inputName = checkbox.name;
  let sourceParam = 'tag';

  if (inputName === 'filter.height') {
    sourceParam = 'height';
  } else if (inputName === 'filter.product') {
    sourceParam = 'product';
  } else if (inputName === 'filter.keyword') {
    sourceParam = 'keyword';
  } else if (inputName === 'filter.tag') {
    sourceParam = 'tag';
  }

  if (checkbox.checked) {
    snapManager.addFilter(filterValue, sourceParam);
  } else {
    snapManager.removeFilter(filterValue);
  }
}

function clearAllFiltersWithURL() {
  snapManager?.clearAllFilters();
}

function changePageNumWithURL(pageNum) {
  snapManager?.changePage(pageNum);
}
